import R from 'ramda';
import { combineReducers } from 'redux';
import { createSelector } from 'reselect';

import * as actions from '../actions';
import { $getTecnicalInformations } from './techDoc';
import { $getAllCountries } from './countries';
import { getQuestionnaireById } from './questionnaires';
import { getContractById } from './contracts';

function byId(state = {}, action) {
  switch (action.type) {
    case actions.REQUEST_COMPANY_ERROR:
    case actions.REQUEST_COMPANYLIST_ERROR:
      return {};
    case actions.REQUEST_COMPANY_SUCCESS:
    case actions.EDIT_COMPANY_SUCCESS: {
      const company = state[action.data.result];
      return !company
        ? Object.assign({}, state, action.data.entities.companies)
        : R.mapObjIndexed(
          (cmp, key) => Object.assign({}, cmp, action.data.entities.companies[key]),
          state);
    }
    case actions.REQUEST_COMPANYLIST_SUCCESS:
      return Object.assign({}, state, action.data.items.entities.companies || {});
    case actions.REQUEST_USERLIST_SUCCESS:
    case actions.REVIEWING_ASK_SUCCESS:
      return Object.assign(state,
        (action.data.items.entities && action.data.items.entities.companies) || {});
    case actions.REQUEST_TECHDOCLIST_SUCCESS: {
      const company = state[action.data.companyId] || {};
      return Object.assign({}, state, {
        [action.data.companyId]:
          Object.assign({}, company, { technicalInformations: action.data.result }),
      });
    }
    case actions.REQUEST_REVIEWING_QUESTIONNAIRES_SUCCESS: {
      return Object.assign({}, state, action.data.items.entities.companies);
    }
    case actions.REQUEST_QUESTIONNAIRELIST_SUCCESS: {
      const company = state[action.data.companyId] || {};
      return Object.assign({}, state, {
        [action.data.companyId]:
          Object.assign({}, company, { questionnaires: action.data.items.result }),
      });
    }
    case actions.REQUEST_BUSINESSINFO_SUCCESS: {
      const company = state[action.data.companyId] || {};
      return Object.assign({}, state, {
        [action.data.companyId]:
          Object.assign({}, company, { businessInformations: action.data.items.result }),
      });
    }
    case actions.REQUEST_COMPANY_STRUCTURE_SUCCESS: {
      const company = state[action.companyId] || {};
      return action.data.entities &&
        action.data.entities.companies &&
        action.data.entities.companies[action.companyId]
        ? Object.assign({}, state, {
          [action.companyId]:
            Object.assign({},
              company,
              action.data.entities && action.data.entities.companies[action.companyId],
              {
                structure: {
                  masters: action.data.masters,
                  directors: action.data.directors,
                  buyers: action.data.buyers,
                  bases: action.data.bases,
                },
              },
            ),
        })
        : Object.assign({}, state);
    }
    case actions.REQUEST_BUSINESSINFO_ORDER_SUCCESS: {
      const { entities, result } = action.data;
      const bi = entities.businessInformations[result];
      const company = state[bi.company.id] || {};
      const bis = R.uniq(Object.assign([], company.businessInformations || [], [result]));
      return Object.assign({}, state, {
        [bi.company.id]:
          Object.assign({}, company, { businessInformations: bis }),
      });
    }
    case actions.CREATE_TECHDOC_SUCCESS: {
      const company = state[action.data.companyId] || {};
      const tis = R.prepend(action.data.response.result, company.technicalInformations);
      return Object.assign({}, state, {
        [action.data.companyId]:
          Object.assign({}, company, { technicalInformations: tis }),
      });
    }
    case actions.REMOVE_TECHDOC_SUCCESS: {
      const company = state[action.companyId] || {};
      const tis = R.filter(id => id !== action.techDocId, company.technicalInformations);
      return Object.assign({}, state, {
        [action.companyId]:
          Object.assign({}, company, { technicalInformations: tis }),
      });
    }
    case actions.ADD_COMPANY_SUCCESS:
      return Object.assign({}, state, action.data.entities.companies);
    case actions.REQUEST_COMPANY_CONTRACTS_SUCCESS: {
      const company = state[action.companyId] || {};
      return Object.assign({}, state, {
        [action.companyId]:
          Object.assign({}, company, { contracts: action.data.result }),
      });
    }
    case actions.CREATE_CONTRACT_SUCCESS: {
      const company = state[action.companyId] || {};
      const contracts = R.append(action.data.result, company.contracts || []);
      return Object.assign({}, state, {
        [action.companyId]:
          Object.assign({}, company, { contracts }),
      });
    }
    case actions.REQUEST_ORDER_SUCCESS:
    case actions.PLACE_ORDER_NATIONAL_SUCCESS:
    case actions.REQUEST_QUESTIONNAIRE_SUCCESS:
    case actions.UPDATE_QUESTIONNAIRE_SUCCESS:
    case actions.SEND_QUESTIONNAIRE_SUCCESS:
      return Object.assign({}, state, action.data.response.entities.companies);
    case actions.REQUEST_COMPANY_BUDGETS_SUCCESS: {
      const mergedCompanies = R.mapObjIndexed((c, id) => {
        const company = state[id] || {};
        return Object.assign({}, company, c);
      }, action.data.entities.companies);

      const budgetsCompany = mergedCompanies[action.companyId] || state[action.companyId] || {};
      mergedCompanies[action.companyId] = Object.assign({}, budgetsCompany, {
        budgets: action.data.result,
        activeContract: R.keys(action.data.entities.contracts).length > 0
          ? R.values(action.data.entities.contracts)[0].id
          : undefined,
      });

      return Object.assign({}, state, mergedCompanies);
    }
    case actions.CREATE_COMPANY_BUDGET_SUCCESS: {
      const company = state[action.companyId] || {};
      const budgets = R.append(action.data.result, company.budgets || []);
      return Object.assign({}, state, {
        [action.companyId]:
          Object.assign({}, company, { budgets }),
      });
    }
    case actions.RESEND_ACCOUNT_ACTIVATION_EMAIL_SUCCESS:
      return Object.assign({}, state, action.data.entities.companies || {});
    default:
      return Object.assign({}, state);
  }
}

function ids(state = [], action) {
  switch (action.type) {
    case actions.REQUEST_COMPANY_START:
    case actions.REQUEST_COMPANY_ERROR:
    case actions.REQUEST_COMPANYLIST_START:
    case actions.REQUEST_COMPANYLIST_ERROR:
      return [];
    case actions.REQUEST_COMPANY_SUCCESS:
      return Object.assign([], state, [action.data.result]);
    case actions.REQUEST_COMPANYLIST_SUCCESS:
      return action.data.items.result || [];
    case actions.REQUEST_USERLIST_SUCCESS:
    case actions.REVIEWING_ASK_SUCCESS:
      return Object.assign([],
        state,
        R.keys((action.data.items.entities && action.data.items.entities.companies) || {}));
    case actions.ADD_COMPANY_SUCCESS:
      return Object.assign([], state, [action.data.result]);
    default:
      return Object.assign([], state);
  }
}

function loading(state = false, action) {
  switch (action.type) {
    case actions.REQUEST_COMPANY_START:
    case actions.REQUEST_COMPANYLIST_START:
    case actions.ADD_COMPANY:
    case actions.EDIT_COMPANY_START:
      return true;
    case actions.REQUEST_COMPANY_ERROR:
    case actions.REQUEST_COMPANYLIST_ERROR:
    case actions.REQUEST_COMPANY_SUCCESS:
    case actions.REQUEST_COMPANYLIST_SUCCESS:
    case actions.ADD_COMPANY_SUCCESS:
    case actions.ADD_COMPANY_ERROR:
    case actions.EDIT_COMPANY_SUCCESS:
    case actions.EDIT_COMPANY_ERROR:
      return false;
    default:
      return state;
  }
}

function error(state = null, action) {
  switch (action.type) {
    case actions.REQUEST_COMPANY_START:
    case actions.REQUEST_COMPANYLIST_START:
    case actions.REQUEST_COMPANY_SUCCESS:
    case actions.REQUEST_COMPANYLIST_SUCCESS:
    case actions.ADD_COMPANY:
    case actions.ADD_COMPANY_SUCCESS:
    case actions.EDIT_COMPANY_START:
    case actions.EDIT_COMPANY_SUCCESS:
      return null;
    case actions.REQUEST_COMPANY_ERROR:
    case actions.REQUEST_COMPANYLIST_ERROR:
    case actions.ADD_COMPANY_ERROR:
    case actions.EDIT_COMPANY_ERROR:
      return action.error || process.env.ERR_GENERIC;
    default:
      return state;
  }
}

function total(state = 0, action) {
  switch (action.type) {
    case actions.REQUEST_COMPANYLIST_START:
    case actions.REQUEST_COMPANYLIST_ERROR:
      return 0;
    case actions.REQUEST_COMPANYLIST_SUCCESS:
      return action.data.total;
    default:
      return state;
  }
}

const companies = combineReducers({
  byId,
  ids,
  loading,
  error,
  total,
});

export default companies;

export const $getIds = state => state.companies.ids;
export const $getCompanies = state => state.companies.byId;
export const $getCompanyById = (state, id) => state.companies.byId[id];
export const $getLoading = state => state.companies.loading;
export const $getTotal = state => state.companies.total;

export const getCompanies = createSelector(
  $getIds, $getCompanies, $getTecnicalInformations, $getAllCountries,
  ($ids, $companies, $technicalInformations, $countries) => ($ids || []).map(id =>
    Object.assign({}, $companies[id], {
      technicalInformations: ($companies[id].techincalInformations || [])
        .map(techId => $technicalInformations[techId]),
      country: $countries[$companies[id].country],
    })));

export const getCompanyById = (state, id) => {
  const $company = state.companies.byId[id];

  return $company && Object.assign({}, $company, {
    technicalInformations: ($company.technicalInformations || [])
      .map(techId => state.technicalInformations.byId[techId]),
    businessInformations: ($company.businessInformations || [])
      .map(biId => state.businessInformations.byId[biId]),
    questionnaires: ($company.questionnaires || [])
      .map(qId => getQuestionnaireById(state, qId)),
    country: state.countries.byId[$company.country],
    contracts: ($company.contracts || [])
      .map(cId => getContractById(state, cId)),
  });
};

export const getLightCompanyById = (state, id) => {
  const $company = state.companies.byId[id];

  return $company && Object.assign({}, $company, {
    country: state.countries.byId[$company.country],
  });
};

// export const getCompanyById = createSelector(
//   $getCompanyById,
//   $getCompanies,
//   $getTecnicalInformations,
//   $getAllCountries,
//   ($company, $technicalInformations, $countries) => $company && Object.assign({}, $company, {
//     technicalInformations: ($company.technicalInformations || [])
//       .map(techId => $technicalInformations[techId]),
//     country: $countries[$company.country],
//   }));

export const isLoading = createSelector(
  $getLoading,
  $loading => $loading);

export const getTotal = createSelector(
  $getTotal,
  $total => $total);
